import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { medicalStatus } from "@/lib/medical";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, FileDown, FileSpreadsheet, Paperclip, IdCard, ArrowUpDown, ArrowUp, ArrowDown, LayoutDashboard, Upload, Loader2, Images } from "lucide-react";
import { toast } from "sonner";
import BulkPhotosDialog from "@/components/BulkPhotosDialog";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import ImportPreviewDialog from "@/components/ImportPreviewDialog";
import PlayerCombobox from "@/components/PlayerCombobox";
import MinutesBreakdownDialog from "@/components/MinutesBreakdownDialog";
import { MIN_A, MIN_B, SCOPES, DEFAULT_SCOPE, scopeToPhase } from "@/lib/minutesStats";
import { exportPlayerPdf, exportPlayerXlsx, sharePlayerCardWhatsApp, exportAllPlayersXlsx } from "@/lib/export";

async function cropSquare(file, size = 512) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new window.Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const side = Math.min(img.width, img.height);
    const sx = (img.width - side) / 2;
    const sy = (img.height - side) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], "foto_jugador.jpg", { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}


const POSITIONS = [
  "Portero",
  "Lateral",
  "Central",
  "Medio centro",
  "Interior",
  "Extremo",
  "Media punta",
  "Delantero",
];

export default function PlayersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selTeam, setSelTeam] = useState("");
  const [players, setPlayers] = useState([]);
  const [statsById, setStatsById] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterYear, setFilterYear] = useState("__all__");
  const [filterPos, setFilterPos] = useState("__all__");
  const [filterPlayer, setFilterPlayer] = useState("");
  const [filterStatus, setFilterStatus] = useState(() => new URLSearchParams(window.location.search).get("estado") || "");
  const [filterMedical, setFilterMedical] = useState(() => new URLSearchParams(window.location.search).get("medical") || "");
  const [importPreview, setImportPreview] = useState(null);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [sortKey, setSortKey] = useState("dorsal");
  const [minScope, setMinScope] = useState(DEFAULT_SCOPE);
  const [breakdown, setBreakdown] = useState(null); // {playerId, playerName}
  const [sortDir, setSortDir] = useState("asc");
  const importRef = useRef(null);
  const [importingPlayers, setImportingPlayers] = useState(false);
  const [form, setForm] = useState({
    name: "", birth_year: "", position: "", secondary_position: "", dorsal: "", team_id: "",
    phone: "", phone_mother: "", phone_father: "", photo_id: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [bulkPhotosOpen, setBulkPhotosOpen] = useState(false);
  const [docsFor, setDocsFor] = useState(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    api.get("/teams").then((r) => {
      setTeams(r.data);
      const qp = new URLSearchParams(window.location.search).get("team_id");
      const saved = localStorage.getItem("app_team_filter");
      setSelTeam((s) => {
        if (s) return s;
        if (qp && r.data.some((t) => t.id === qp)) return qp;
        if (saved === "__all__" && user?.role === "admin") return "__all__";
        if (saved && r.data.some((t) => t.id === saved)) return saved;
        return user?.role === "admin" ? "__all__" : (r.data[0]?.id || "");
      });
    });
  }, []);

  const pickTeam = (v) => {
    setSelTeam(v);
    localStorage.setItem("app_team_filter", v);
    setFilterPlayer("");
  };

  const load = () => {
    if (!selTeam) return;
    if (selTeam === "__all__") {
      api.get(`/players`).then((r) => setPlayers(r.data));
    } else {
      api.get(`/players?team_id=${selTeam}`).then((r) => setPlayers(r.data));
    }
    // Enrich table with training / minute percentages.
    const phase = scopeToPhase(minScope);
    const base = selTeam === "__all__" ? "/stats/dashboard" : `/stats/dashboard?team_id=${selTeam}`;
    const statsUrl = phase ? `${base}${base.includes("?") ? "&" : "?"}phase=${phase}` : base;
    api.get(statsUrl)
      .then((r) => {
        const map = {};
        for (const s of r.data?.by_player || []) {
          map[s.player_id] = {
            attendance_pct: s.attendance_pct ?? 0,
            minutes_pct_total: s.minutes_pct_total ?? 0,
            minutes_pct_called: s.minutes_pct_called ?? 0,
            played_with_record: s.played_with_record ?? 0,
            matches_called: s.matches_called ?? 0,
          };
        }
        setStatsById(map);
      })
      .catch(() => setStatsById({}));
  };
  useEffect(() => { load(); }, [selTeam, minScope]);

  // Quick lookup: player.team_id → team name (used when filter = All teams).
  const teamNameById = React.useMemo(
    () => Object.fromEntries(teams.map((t) => [t.id, t.name])),
    [teams],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "", birth_year: "", position: "", secondary_position: "", dorsal: "", team_id: selTeam,
      phone: "", phone_mother: "", phone_father: "", photo_id: "", rffm_player_code: "", medical_expiry: "", status: "disponible",
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, birth_year: p.birth_year ?? "", position: p.position || "",
      secondary_position: p.secondary_position || "",
      dorsal: p.dorsal ?? "", team_id: p.team_id,
      phone: p.phone || "", phone_mother: p.phone_mother || "", phone_father: p.phone_father || "",
      photo_id: p.photo_id || "", rffm_player_code: p.rffm_player_code || "", medical_expiry: p.medical_expiry || "",
      status: p.status || "disponible",
    });
    setPhotoFile(null);
    setPhotoPreview(p.photo_id ? `${process.env.REACT_APP_BACKEND_URL}/api/attachments/${p.photo_id}/file` : null);
    setOpen(true);
  };

  const onPickPhoto = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    const ext = (raw.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WebP.");
      return;
    }
    const squared = await cropSquare(raw);
    setPhotoFile(squared);
    setPhotoPreview(URL.createObjectURL(squared));
  };

  const uploadPhoto = async (playerId, file) => {
    const fd = new FormData();
    fd.append("scope", "player_photo");
    fd.append("scope_id", playerId);
    fd.append("file", file);
    const r = await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      birth_year: form.birth_year ? parseInt(form.birth_year, 10) : null,
      position: form.position,
      secondary_position: form.secondary_position || "",
      dorsal: form.dorsal ? parseInt(form.dorsal, 10) : null,
      team_id: form.team_id,
      phone: form.phone || "",
      phone_mother: form.phone_mother || "",
      phone_father: form.phone_father || "",
      photo_id: form.photo_id || "",
      medical_expiry: form.medical_expiry || "",
      status: form.status || "disponible",
      rffm_player_code: (form.rffm_player_code || "").trim(),
    };
    try {
      if (editing) {
        if (photoFile) payload.photo_id = await uploadPhoto(editing.id, photoFile);
        await api.put(`/players/${editing.id}`, payload);
      } else {
        const r = await api.post("/players", payload);
        if (photoFile && r.data?.id) {
          payload.photo_id = await uploadPhoto(r.data.id, photoFile);
          await api.put(`/players/${r.data.id}`, payload);
        }
      }
      toast.success(editing ? "Jugador actualizado" : "Jugador creado");
      const prevCode = editing?.rffm_player_code || "";
      if (payload.rffm_player_code && payload.rffm_player_code !== prevCode) {
        toast.info("Sincronizando datos RFFM de las últimas 3 temporadas…", { duration: 6000 });
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name}?`)) return;
    await api.delete(`/players/${p.id}`);
    toast.success("Jugador eliminado");
    load();
  };

  const canEdit = user?.role === "admin" || (user?.team_ids || []).includes(selTeam);

  const importPlayersFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selTeam || selTeam === "__all__") return;
    setImportingPlayers(true);
    try {
      const fd = new FormData();
      fd.append("team_id", selTeam);
      fd.append("file", file);
      const r = await api.post("/players/import-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportingPlayers(false);
    }
  };

  const confirmImport = async (decisions) => {
    setConfirmingImport(true);
    try {
      const r = await api.post("/players/import-file/confirm", {
        staging_id: importPreview.staging_id,
        decisions,
      });
      const { created, updated, unchanged, skipped } = r.data;
      toast.success(`Importación completada: ${created} nuevos, ${updated} actualizados, ${unchanged} sin cambios, ${skipped} omitidos`);
      setImportPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirmingImport(false);
    }
  };

  // Distinct birth years in the current squad (sorted desc).
  const yearOptions = Array.from(
    new Set(players.map((p) => p.birth_year).filter((v) => v && v > 0)),
  ).sort((a, b) => b - a);

  // Distinct positions actually used by the current squad.
  const positionOptions = Array.from(
    new Set(players.map((p) => p.position).filter(Boolean)),
  ).sort();

  const filteredPlayers = players.filter((p) => {
    if (filterPlayer && p.id !== filterPlayer) return false;
    if (filterStatus && (p.status || "disponible") !== filterStatus) return false;
    if (filterMedical) {
      const ms = medicalStatus(p.medical_expiry);
      if (filterMedical === "sin_registro" && p.medical_expiry) return false;
      if (filterMedical === "caducado" && ms?.level !== "rojo") return false;
      if (filterMedical === "por_caducar" && !["amarillo", "naranja"].includes(ms?.level)) return false;
    }
    if (filterYear !== "__all__" && String(p.birth_year ?? "") !== filterYear) return false;
    if (filterPos !== "__all__" && (p.position || "") !== filterPos) return false;
    return true;
  });

  // Sorting: click a column header to toggle asc/desc.
  const _valFor = (p, key) => {
    if (key === "att") return statsById[p.id]?.attendance_pct ?? -1;
    if (key === "min_total") return statsById[p.id]?.minutes_pct_total ?? -1;
    if (key === "min_called") return statsById[p.id]?.minutes_pct_called ?? -1;
    if (key === "team") return (teamNameById[p.team_id] || "").toLowerCase();
    if (key === "name") return (p.name || "").toLowerCase();
    if (key === "position") return (p.position || "").toLowerCase();
    if (key === "dorsal") return p.dorsal ?? 9999;
    if (key === "birth_year") return p.birth_year ?? 0;
    return "";
  };
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const va = _valFor(a, sortKey);
    const vb = _valFor(b, sortKey);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const sortIconFor = (column) => {
    if (sortKey !== column) return <ArrowUpDown className="h-3 w-3 inline-block ml-1 text-slate-400" />;
    return sortDir === "asc"
      ? <ArrowUp className="h-3 w-3 inline-block ml-1 text-purple-700" />
      : <ArrowDown className="h-3 w-3 inline-block ml-1 text-purple-700" />;
  };
  const sortableHead = (label, key, extra = "") => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 ${extra}`} onClick={() => toggleSort(key)} data-testid={`sort-${key}`}>
      {label}{sortIconFor(key)}
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>Jugadores</h1>
          <p className="text-slate-500 text-sm mt-1">Gestiona la plantilla de cada equipo.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {teams.length > 0 && (
            <Select value={selTeam} onValueChange={pickTeam}>
              <SelectTrigger className="w-56" data-testid="players-team-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {user?.role === "admin" && (
                  <SelectItem value="__all__">Todos los equipos</SelectItem>
                )}
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {selTeam === "__all__" && user?.role === "admin" && (
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={exportAllPlayersXlsx}
              data-testid="export-all-players-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar Excel
            </Button>
          )}
          {canEdit && selTeam && selTeam !== "__all__" && (
            <>
              <input
                ref={importRef}
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xlsm,.pdf,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={importPlayersFile}
                data-testid="import-players-input"
              />
              <Button
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => importRef.current?.click()}
                disabled={importingPlayers}
                data-testid="import-players-button"
              >
                {importingPlayers
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Upload className="h-4 w-4 mr-2" />} Importar Excel/PDF
              </Button>
              <Button
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => setBulkPhotosOpen(true)}
                data-testid="bulk-photos-button"
              >
                <Images className="h-4 w-4 mr-2" /> Fotos masivas
              </Button>
            </>
          )}
          {canEdit && selTeam && selTeam !== "__all__" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-player-button">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo jugador
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editing ? "Editar jugador" : "Nuevo jugador"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  <div className="flex items-center gap-4">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      onChange={onPickPhoto}
                      className="hidden"
                      data-testid="player-photo-input"
                    />
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Foto del jugador"
                        className="w-16 h-16 rounded-full object-cover border-2 border-purple-200 shrink-0"
                        data-testid="player-photo-preview"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-purple-50 border-2 border-dashed border-purple-200 grid place-items-center text-purple-400 text-xl font-black shrink-0">
                        {form.name ? form.name.charAt(0).toUpperCase() : "?"}
                      </div>
                    )}
                    <div className="flex flex-col gap-1.5">
                      <Button type="button" size="sm" variant="outline"
                        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => photoInputRef.current?.click()}
                        data-testid="player-photo-btn">
                        {photoPreview ? "Cambiar foto" : "Subir foto"}
                      </Button>
                      {photoPreview && (
                        <Button type="button" size="sm" variant="ghost"
                          className="text-xs text-red-600 hover:bg-red-50"
                          onClick={() => { setPhotoFile(null); setPhotoPreview(null); setForm({ ...form, photo_id: "" }); }}
                          data-testid="player-photo-remove">
                          Quitar
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Nombre</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="player-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Año de nacimiento</Label>
                      <Input type="number" min="1990" max="2030" value={form.birth_year} onChange={(e) => setForm({ ...form, birth_year: e.target.value })} placeholder="Ej. 2014" data-testid="player-birth-year-input" />
                    </div>
                    <div>
                      <Label>Dorsal</Label>
                      <Input type="number" value={form.dorsal} onChange={(e) => setForm({ ...form, dorsal: e.target.value })} data-testid="player-dorsal-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Posición principal</Label>
                    <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                      <SelectTrigger data-testid="player-position-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Segunda posición <span className="text-slate-400 text-xs">(opcional)</span></Label>
                    <Select
                      value={form.secondary_position || "__none__"}
                      onValueChange={(v) => setForm({ ...form, secondary_position: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="player-secondary-position-select">
                        <SelectValue placeholder="Sin segunda posición" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin segunda posición</SelectItem>
                        {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Teléfono <span className="text-slate-400 text-xs">(opcional)</span></Label>
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="612 345 678"
                        data-testid="player-phone-input"
                      />
                    </div>
                    <div>
                      <Label>Tel. madre <span className="text-slate-400 text-xs">(opcional)</span></Label>
                      <Input
                        type="tel"
                        value={form.phone_mother}
                        onChange={(e) => setForm({ ...form, phone_mother: e.target.value })}
                        placeholder="612 345 678"
                        data-testid="player-phone-mother-input"
                      />
                    </div>
                    <div>
                      <Label>Tel. padre/tutor <span className="text-slate-400 text-xs">(opcional)</span></Label>
                      <Input
                        type="tel"
                        value={form.phone_father}
                        onChange={(e) => setForm({ ...form, phone_father: e.target.value })}
                        placeholder="612 345 678"
                        data-testid="player-phone-father-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Reconocimiento médico <span className="text-slate-400 text-xs">(caducidad)</span></Label>
                      <Input
                        type="date"
                        value={form.medical_expiry}
                        onChange={(e) => setForm({ ...form, medical_expiry: e.target.value })}
                        data-testid="player-medical-input"
                      />
                    </div>
                    <div>
                      <Label>Estado</Label>
                      <Select value={form.status || "disponible"} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="player-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disponible">Disponible</SelectItem>
                          <SelectItem value="lesionado">Lesionado</SelectItem>
                          <SelectItem value="enfermo">Enfermo</SelectItem>
                          <SelectItem value="sancionado">Sancionado</SelectItem>
                          <SelectItem value="baja">Baja</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Código Federación (RFFM) <span className="text-slate-400 text-xs">(opcional)</span></Label>
                    <Input
                      value={form.rffm_player_code}
                      onChange={(e) => setForm({ ...form, rffm_player_code: e.target.value })}
                      placeholder="Ej. 14588293 o URL de la ficha rffm.es"
                      data-testid="player-rffm-code-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Al guardar se sincronizan automáticamente sus últimas 3 temporadas oficiales (visible en el panel del jugador).
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="player-save-button">Guardar</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {players.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              {selTeam === "__all__" ? "No hay jugadores registrados." : "No hay jugadores en este equipo."}
            </div>
          ) : (
            <>
              <div className="p-4 flex flex-wrap items-end gap-3 border-b border-slate-100">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Filtros</div>
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Año nac.</Label>
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="w-36" data-testid="filter-birth-year"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos los años</SelectItem>
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Posición</Label>
                  <Select value={filterPos} onValueChange={setFilterPos}>
                    <SelectTrigger className="w-44" data-testid="filter-position"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas las posiciones</SelectItem>
                      {positionOptions.map((pos) => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Jugador</Label>
                  <PlayerCombobox
                    players={players}
                    value={filterPlayer}
                    onChange={setFilterPlayer}
                    testId="filter-player-combobox"
                  />
                </div>
                {(filterYear !== "__all__" || filterPos !== "__all__" || filterPlayer || filterStatus || filterMedical) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setFilterYear("__all__"); setFilterPos("__all__"); setFilterPlayer(""); setFilterStatus(""); setFilterMedical(""); }}
                    className="text-xs text-purple-600 hover:bg-purple-50"
                    data-testid="clear-filters"
                  >
                    Limpiar filtros
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Select value={minScope} onValueChange={setMinScope}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid="players-min-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-500" data-testid="players-result-count">
                    {filteredPlayers.length} / {players.length} jugadores
                  </span>
                </div>
              </div>
              <Table>
              <TableHeader>
                <TableRow>
                  {sortableHead("Dorsal", "dorsal", "w-16")}
                  {sortableHead("Nombre", "name")}
                  {selTeam === "__all__" && sortableHead("Equipo", "team")}
                  {sortableHead("Posición", "position")}
                  {sortableHead("Año nac.", "birth_year")}
                  {sortableHead("% entren.", "att", "text-right")}
                  {sortableHead("% min. equipo", "min_total", "text-right")}
                  {sortableHead("% min. conv.", "min_called", "text-right")}
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPlayers.map((p) => {
                  const st = statsById[p.id] || {};
                  const attPct = st.attendance_pct ?? null;
                  const minTotal = st.played_with_record ? (st.minutes_pct_total ?? null) : null;
                  const minCalled = st.matches_called ? (st.minutes_pct_called ?? null) : null;
                  const pctBadge = (v) => {
                    if (v === null || v === undefined) return "bg-slate-100 text-slate-400";
                    if (v >= 70) return "bg-emerald-100 text-emerald-800";
                    if (v >= 40) return "bg-amber-100 text-amber-800";
                    return "bg-rose-100 text-rose-800";
                  };
                  return (
                  <TableRow key={p.id} data-testid={`player-row-${p.id}`}>
                    <TableCell>
                      <span className="inline-flex w-8 h-8 rounded-full bg-purple-100 text-purple-800 font-black items-center justify-center text-sm">
                        {p.dorsal ?? "-"}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        {p.photo_id && (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${p.photo_id}/file`}
                            alt=""
                            className="w-11 h-11 rounded-full object-cover border border-purple-200 shrink-0"
                            data-testid={`player-list-photo-${p.id}`}
                          />
                        )}
                        <span>{p.name}</span>
                        {(() => {
                          const ms = medicalStatus(p.medical_expiry);
                          if (!ms || ms.level === "ok") return null;
                          return (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-black whitespace-nowrap ${ms.cls}`}
                              title={`Reconocimiento médico: ${ms.level === "rojo" ? "caducado el" : "caduca el"} ${ms.date}`}
                              data-testid={`player-medical-badge-${p.id}`}
                            >
                              RM {ms.label}
                            </span>
                          );
                        })()}
                        {p.status && p.status !== "disponible" && (
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            { lesionado: "bg-rose-100 text-rose-700", enfermo: "bg-amber-100 text-amber-700",
                              sancionado: "bg-purple-100 text-purple-700", baja: "bg-slate-200 text-slate-600" }[p.status] || "bg-slate-100 text-slate-600"
                          }`} data-testid={`player-status-badge-${p.id}`}>
                            {p.status}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {selTeam === "__all__" && (
                      <TableCell>
                        <span className="text-xs font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" data-testid={`player-team-${p.id}`}>
                          {teamNameById[p.team_id] || "—"}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>{p.position || "-"}</TableCell>
                    <TableCell>{p.birth_year ?? "-"}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => navigate(`/players/${p.id}/detalle-asistencia?scope=no_pre`)} className={`text-xs font-bold px-2 py-0.5 rounded-full hover:ring-2 hover:ring-purple-300 ${pctBadge(attPct)}`} data-testid={`player-att-pct-${p.id}`}>
                        {attPct === null ? "—" : `${attPct}%`}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => setBreakdown({ playerId: p.id, playerName: p.name })}
                        className={`text-xs font-bold px-2 py-0.5 rounded-full border hover:ring-2 hover:ring-blue-200 ${minTotal === null ? "bg-slate-100 text-slate-400 border-slate-200" : MIN_A.chip}`}
                        data-testid={`player-min-total-${p.id}`}>
                        {minTotal === null ? "Sin datos" : `${String(minTotal).replace(".", ",")}%`}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => setBreakdown({ playerId: p.id, playerName: p.name })}
                        className={`text-xs font-bold px-2 py-0.5 rounded-full border hover:ring-2 hover:ring-purple-200 ${minCalled === null ? "bg-slate-100 text-slate-400 border-slate-200" : MIN_B.chip}`}
                        data-testid={`player-min-called-${p.id}`}>
                        {minCalled === null ? "Sin datos" : `${String(minCalled).replace(".", ",")}%`}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/players/${p.id}/dashboard`)} data-testid={`dashboard-player-${p.id}`} title="Dashboard del jugador">
                        <LayoutDashboard className="h-4 w-4 text-indigo-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => exportPlayerPdf(p)} data-testid={`export-player-${p.id}`} title="Exportar PDF">
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => exportPlayerXlsx(p)} data-testid={`export-player-xlsx-${p.id}`} title="Exportar Excel">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDocsFor(p)} data-testid={`docs-player-${p.id}`} title="Imágenes, documentos y vídeos">
                        <Paperclip className="h-4 w-4 text-purple-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => sharePlayerCardWhatsApp(p, teams.find((t) => t.id === p.team_id))} data-testid={`card-player-${p.id}`} title="Carnet del jugador (compartir)">
                        <IdCard className="h-4 w-4 text-rose-500" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-player-${p.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-player-${p.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      <BulkPhotosDialog
        open={bulkPhotosOpen}
        onOpenChange={setBulkPhotosOpen}
        players={players.filter((p) => selTeam === "__all__" || p.team_id === selTeam)}
        onDone={load}
      />

      {importPreview && (
        <ImportPreviewDialog
          preview={importPreview}
          onClose={() => setImportPreview(null)}
          onConfirm={confirmImport}
          loading={confirmingImport}
        />
      )}

      <Dialog open={!!docsFor} onOpenChange={(o) => !o && setDocsFor(null)}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Archivos de {docsFor?.name}</DialogTitle>
          </DialogHeader>
          {docsFor && (
            <AttachmentsPanel
              scope="player_doc"
              scopeId={docsFor.id}
              canEdit={user?.role === "admin" || (user?.team_ids || []).includes(docsFor.team_id)}
              allowLinks
              maxFiles={10}
              title="Imágenes, documentos y vídeos"
              hint="JPG, PNG, PDF, DOC, DOCX, PPT, PPTX · máx. 10 MB · o añade un enlace de vídeo"
              emptyLabel="Aún no hay archivos ni vídeos de este jugador."
            />
          )}
        </DialogContent>
      </Dialog>
      <MinutesBreakdownDialog
        open={!!breakdown}
        onClose={() => setBreakdown(null)}
        playerId={breakdown?.playerId}
        playerName={breakdown?.playerName}
        scope={minScope}
      />
    </div>
  );
}
